import { Tabs } from '@acpaas-ui/react-components';
import _ from 'lodash';
import queryString from 'query-string';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, Link, Route, Routes, useLocation, useParams } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';
import { fetchReset } from '../../actions/fetch.actions';
import DossierDetailsFetching from '../../components/dossier-details/DossierDetailsFetching';
import DossierHeader from '../../components/dossier-details/DossierHeader';
import { DOSSIER_DETAIL_BREADCRUMBS } from '../../constants/breadcrumbs.constants';
import { DOSSIER_RESOURCES } from '../../constants/dossier.constants';
import { MODALS } from '../../constants/modals.constants';
import {
  DOSSIER_DETAIL_PATH,
  DOSSIER_DETAIL_TAB_PATHS,
  DOSSIER_DETAIL_TAB_PATHS_BY_TYPE
} from '../../constants/paths.constants';
import { TOAST_TYPES } from '../../constants/settings.constants';
import { useDossierRouteGuard } from '../../hooks/dossiers/useDossierRouteGuard.hook';
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs.hook';
import { useModal } from '../../hooks/useModal.hook';
import { usePreferences } from '../../hooks/usePreferences.hook';
import { useSingleResource } from '../../hooks/useSingleResource.hook';
import { useTableResource } from '../../hooks/useTableResource.hook';
import AssignToDossierModal from '../../modals/AssignToDossierModal';
import StatusChangeModal from '../../modals/StatusChangeModal';
import { CASETAFEL_FORM_OPTIONS_RESOURCE } from '../../resources/casetafel.form.options.resource';
import { DOSSIER_ACTIVITY_RESOURCE } from '../../resources/dossier.activity.resource';
import { DOSSIER_ATTACHMENTS_RESOURCE } from '../../resources/dossier.attachments.resource';
import { DOSSIERS_RESOURCE } from '../../resources/dossiers.resource';
import { currentUserSelector } from '../../selectors/auth.selectors';
import dossiersService from '../../services/api/dossiers.service';
import { attachments as attachmentsBff, forms } from '../../services/bff.api';
import permissionsService from '../../services/permissions/permissions.service';
import toastService from '../../services/toasts.service';
import DossierSidebar from '../dossier-sidebar/DossierSidebar';
import { usePrevious } from '../../hooks/usePrevious';

const DossierDetails = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const prevDossierId = usePrevious(params.id);
  const location = useLocation();
  const pathname = location.pathname;
  const { isFetching, isFetched, data, setValueInState, refetch } = useSingleResource(params.id, DOSSIERS_RESOURCE);
  const loadingDossier = !isFetched || isFetching;
  const user = useSelector(currentUserSelector);
  const attachmentsResource = {
    ...DOSSIER_ATTACHMENTS_RESOURCE,
    api: { get: () => attachmentsBff.getForDossier(params.id) }
  };
  const attachmentsPreferences = usePreferences(user, attachmentsResource);
  const { data: attachments, isLoading, onReset } = useTableResource(attachmentsResource, attachmentsPreferences);
  const casetafelFormsResource = {
    ...CASETAFEL_FORM_OPTIONS_RESOURCE,
    api: { get: () => forms.getForCaseTable(data?.type) }
  };
  const { data: casetafelForms, isLoading: isLoadingCasetafelForms } = useTableResource(casetafelFormsResource, {
    isFetched: !!data?.type
  });
  const dossierDetailRootPath = generatePath(DOSSIER_DETAIL_PATH, { id: params.id });

  const [openedClarifications, setOpenedClarifications] = useState([]);
  const [openedCasetables, setOpenedCasetables] = useState([]);
  const [openedGoals, setOpenedGoals] = useState([]);
  const [openedParts, setOpenedParts] = useState([]);
  const [openedIndicators, setOpenedIndicators] = useState([]);
  const statusChangeModal = useModal(MODALS.STATUS_CHANGE);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [isChangingPrivateMode, setIsChangingPrivateMode] = useState(false);
  const [isChangingDeadline, setIsChangingDeadline] = useState(false);
  const assignModal = useModal(MODALS.ASSIGN_TO_DOSSIER_MODAL);

  const permissions = useDossierRouteGuard(loadingDossier, data);
  useBreadcrumbs(DOSSIER_DETAIL_BREADCRUMBS, loadingDossier, data);

  const resetResources = useCallback(() => {
    DOSSIER_RESOURCES.forEach((resource) => dispatch(fetchReset(resource)));
  }, [dispatch]);

  useEffect(() => {
    if (params.id && prevDossierId && params.id !== prevDossierId) {
      refetch();
      resetResources();
    }
  }, [refetch, resetResources, params.id, prevDossierId]);

  useEffect(() => {
    return resetResources;
  }, [resetResources]);

  useEffect(() => {
    const { clarification, casetafel } = queryString.parse(location.search);
    if (clarification) {
      setOpenedClarifications([clarification]);
    }
    if (casetafel) {
      setOpenedCasetables([casetafel]);
    }
  }, [location.search]);

  const _updateArraySubitem = (value, field, path, itemId) => {
    const newSubitemsData = _.cloneDeep(data?.[path]);
    if (newSubitemsData?.length < 1) {
      return;
    }

    const itemIndex = newSubitemsData.findIndex((i) => i.id === itemId);
    if (itemIndex === -1) {
      return;
    }
    newSubitemsData[itemIndex] = { ...newSubitemsData[itemIndex], [field]: value };
    setValueInState(newSubitemsData, path);
  };

  const _updateSingleSubitem = (value, field, path) => {
    const newSubitemData = _.cloneDeep(data?.[path]);
    setValueInState({ ...newSubitemData, [field]: value }, path);
  };

  const onSubitemUpdated = (value, field, path, itemId) => {
    if (itemId) {
      _updateArraySubitem(value, field, path, itemId);
    } else {
      _updateSingleSubitem(value, field, path);
    }
    dispatch(fetchReset(DOSSIER_ACTIVITY_RESOURCE));
  };

  const handleStatusChangeRequest = (status) => {
    setSelectedStatus(status);
    statusChangeModal.onOpen();
  };

  const handleStatusChange = (remark, reason, onError, onSuccess) => {
    dossiersService.statusChange(
      params.id,
      { status: selectedStatus.value, remark, reason },
      {
        onSuccess: () => {
          onSuccess();
          setValueInState(selectedStatus.label, 'status');
          dispatch(fetchReset(DOSSIER_ACTIVITY_RESOURCE));
        },
        onError
      }
    );
  };

  const handleStatusChangeClose = () => {
    setSelectedStatus(null);
    statusChangeModal.onClose();
  };

  const handlePrivateModeToggle = () => {
    setIsChangingPrivateMode(true);
    dossiersService.privateModeChange(params.id, !data?.isPrivate, {
      onError: (message) => {
        toastService.showToast(TOAST_TYPES.ERROR, message);
        setIsChangingPrivateMode(false);
        ReactTooltip.rebuild();
      },
      onSuccess: () => {
        setValueInState(!data?.isPrivate, 'isPrivate');
        setIsChangingPrivateMode(false);
        dispatch(fetchReset(DOSSIER_ACTIVITY_RESOURCE));
        ReactTooltip.rebuild();
      }
    });
  };

  const handleDeadlineChange = (deadlineDate) => {
    setIsChangingDeadline(true);
    dossiersService.deadlineChange(params.id, deadlineDate, {
      onError: (message) => {
        toastService.showToast(TOAST_TYPES.ERROR, message);
        setIsChangingDeadline(false);
      },
      onSuccess: () => {
        setValueInState({ ...data?.overview, deadlineDate }, 'overview');
        setIsChangingDeadline(false);
        dispatch(fetchReset(DOSSIER_ACTIVITY_RESOURCE));
      }
    });
  };

  const handleDossierRefetch = () => {
    refetch();
    resetResources();
  };

  const handleAttachmentsRefetch = () => {
    onReset();
    dispatch(fetchReset(DOSSIER_ACTIVITY_RESOURCE));
  };

  const getIsTabActive = (tab) => {
    const tabPath = `${pathname.replace(dossierDetailRootPath, '')}`;
    return (tabPath === '' && tab.path === DOSSIER_DETAIL_TAB_PATHS.OVERVIEW.path) || tabPath === tab.path;
  };

  const sharedTabProps = {
    data,
    isLoadingAttachments: isLoading,
    onAttachmentsRefetch: handleAttachmentsRefetch,
    onDossierRefetch: handleDossierRefetch,
    onUpdated: onSubitemUpdated,
    setOpenedClarifications,
    openedClarifications,
    setOpenedCasetables,
    openedCasetables,
    setOpenedParts,
    openedParts,
    setOpenedGoals,
    openedGoals,
    setOpenedIndicators,
    openedIndicators,
    attachments,
    user,
    userRoles: user?.permissions ?? [],
    forms: casetafelForms,
    isLoadingForms: isLoadingCasetafelForms,
    permissions
  };

  return (
    <div className="spo-p-dossier-details u-margin-top u-margin-bottom u-margin-right-lg">
      {permissionsService.canSeeDossierSidebar(user) && (
        <DossierSidebar status={data?.status} involved={data?.overview?.involved ?? []} />
      )}
      {loadingDossier ? (
        <DossierDetailsFetching />
      ) : (
        <>
          <DossierHeader
            {...data}
            {...(data?.overview ?? {})}
            user={user}
            isChangingPrivateMode={isChangingPrivateMode}
            isChangingDeadline={isChangingDeadline}
            onStatusChange={handleStatusChangeRequest}
            onPrivateModeToggle={handlePrivateModeToggle}
            onDeadlineChange={handleDeadlineChange}
            onAssignedOpen={assignModal.onOpen}
          />
          <Tabs
            align="left"
            items={
              Object.values(DOSSIER_DETAIL_TAB_PATHS_BY_TYPE?.[data?.type] ?? {})
                .filter((tab) => permissionsService.canAccessDossierTab(user, data, tab))
                .map((tab) => ({
                  name: tab.name,
                  target: tab.path,
                  active: getIsTabActive(tab)
                })) ?? []
            }
            linkProps={(props) => ({
              ...props,
              component: Link,
              to: `${dossierDetailRootPath}${props.href === '/' ? '' : props.href}`
            })}
          />
          <div className="spo-p-dossier-details__tab-content u-padding">
            <Routes>
              {Object.values(DOSSIER_DETAIL_TAB_PATHS)
                .filter((tab) => tab.dossierTypes.includes(data?.type))
                .map((tab) => (
                  <Route key={tab.path} path={tab.path} element={<tab.component {...sharedTabProps} />} />
                ))}
            </Routes>
          </div>
          <StatusChangeModal
            {...statusChangeModal}
            dossierType={data?.type}
            onClose={handleStatusChangeClose}
            status={selectedStatus}
            onConfirm={handleStatusChange}
          />
          {assignModal.isOpen && (
            <AssignToDossierModal
              {...assignModal}
              dossierId={params.id}
              assigned={data?.assigned ?? []}
              onUpdated={onSubitemUpdated}
            />
          )}
        </>
      )}
    </div>
  );
};

export default DossierDetails;
